// src/pages/OrderDetailPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { ordersAPI, ridersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { StatusBadge, LoadingOverlay, QRDisplay, Modal, Spinner, toast } from '../components/UI';
import api from '../services/api';

const STATUS_FLOW = { 'Assigned': 'Picked Up', 'Picked Up': 'In Transit', 'In Transit': 'Delivered' };

// ─── Printable Label ──────────────────────────────────────────────────────────
function PrintLabel({ order }) {
  const printRef = useRef();

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const w = window.open('', '_blank', 'width=600,height=700');
    w.document.write(`
      <!DOCTYPE html><html><head><title>Label - ${order.package_id}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: Arial, sans-serif; background: #fff; }
        .label { width: 540px; border: 2.5px solid #0a1628; border-radius: 12px; overflow: hidden; margin: 20px auto; }
        .label-header { background: #0a1628; padding: 14px 18px; display: flex; justify-content: space-between; align-items: center; }
        .label-logo { font-size: 18px; font-weight: 800; color: #f4a61c; }
        .label-pkg { font-family: monospace; font-size: 13px; color: #94a3b8; }
        .label-body { display: flex; }
        .label-qr { padding: 16px; border-right: 2px dashed #e2e8f0; display: flex; flex-direction: column; align-items: center; gap: 6px; }
        .label-qr-id { font-family: monospace; font-size: 10px; color: #94a3b8; text-align: center; word-break: break-all; max-width: 140px; }
        .label-info { padding: 14px 16px; flex: 1; }
        .label-row { margin-bottom: 10px; }
        .label-row-key { font-size: 9px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 2px; }
        .label-row-val { font-size: 14px; font-weight: 600; color: #0a1628; }
        .label-row-val.lg { font-size: 18px; font-weight: 800; }
        .label-footer { background: #f8fafc; border-top: 2px solid #e2e8f0; padding: 10px 18px; display: flex; justify-content: space-between; font-size: 11px; color: #94a3b8; }
        @media print { body { margin: 0; } .label { margin: 0; border-radius: 0; } }
      </style></head><body>${content}
      <script>setTimeout(()=>{window.print();},300);<\/script></body></html>
    `);
    w.document.close();
  };

  return (
    <div>
      <div ref={printRef} style={{ display: 'none' }}>
        <div className="label">
          <div className="label-header">
            <span className="label-logo">Honcho Xpress</span>
            <span className="label-pkg">{order.package_id}</span>
          </div>
          <div className="label-body">
            <div className="label-qr">
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=130x130&data=${encodeURIComponent(order.qr_code)}&color=0a1628`} width="130" height="130" alt="QR" />
              <div className="label-qr-id">{order.qr_code}</div>
            </div>
            <div className="label-info">
              <div className="label-row">
                <div className="label-row-key">To (Receiver)</div>
                <div className="label-row-val lg">{order.receiver_name}</div>
              </div>
              <div className="label-row">
                <div className="label-row-key">Phone</div>
                <div className="label-row-val">{order.phone}</div>
              </div>
              <div className="label-row">
                <div className="label-row-key">Delivery Address</div>
                <div className="label-row-val">{order.address}</div>
              </div>
              <div className="label-row">
                <div className="label-row-key">From (Sender)</div>
                <div className="label-row-val">{order.sender_name}</div>
              </div>
            </div>
          </div>
          <div className="label-footer">
            <span>Order #{order.id} · {moment(order.created_at).format('MMM D, YYYY')}</span>
            <span>{order.package_id}</span>
          </div>
        </div>
      </div>

      <div style={{ border: '2px solid var(--gray-200)', borderRadius: 10, overflow: 'hidden', fontSize: 13 }}>
        <div style={{ background: 'var(--primary)', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, color: 'var(--accent)', fontSize: 14 }}>Honcho Xpress</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--gray-400)' }}>{order.package_id}</span>
        </div>
        <div style={{ display: 'flex', padding: 14, gap: 14 }}>
          <QRDisplay value={order.qr_code} size={100} />
          <div style={{ flex: 1 }}>
            {[['To', order.receiver_name], ['Phone', order.phone], ['Address', order.address], ['From', order.sender_name]].map(([k, v]) => (
              <div key={k} style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: 0.8 }}>{k}</div>
                <div style={{ fontWeight: 600, color: 'var(--gray-900)' }}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <button className="btn btn-secondary btn-sm" style={{ width: '100%', marginTop: 10 }} onClick={handlePrint}>
        Print Label
      </button>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [order, setOrder] = useState(null);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showFail, setShowFail] = useState(false);
  const [failNote, setFailNote] = useState('');
  const [selRider, setSelRider] = useState('');

  const fetchData = async () => {
    try {
      const [o, r] = await Promise.all([ordersAPI.getById(id), ridersAPI.getAll()]);
      setOrder(o); setRiders(r || []);
    } catch { toast.error('Order not found.'); navigate('/orders'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleStatus = async (status, notes = '') => {
    setActionLoading(true);
    try { await ordersAPI.updateStatus(id, status, notes); toast.success(`Marked as ${status}`); await fetchData(); }
    catch (err) { toast.error(err?.response?.data?.error || 'Failed.'); }
    finally { setActionLoading(false); }
  };

  const handleMarkPaid = async () => {
    setPayLoading(true);
    try {
      await api.patch(`/orders/${id}/payment`, { payment_status: 'Paid' });
      toast.success('Order marked as paid');
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update payment.');
    } finally {
      setPayLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selRider) { toast.error('Select a rider.'); return; }
    setActionLoading(true);
    try { await ordersAPI.assign(id, selRider); toast.success('Rider assigned!'); setShowAssign(false); setSelRider(''); await fetchData(); }
    catch { toast.error('Failed.'); } finally { setActionLoading(false); }
  };

  const handleFail = async () => {
    if (!failNote.trim()) { toast.error('Enter a reason.'); return; }
    setShowFail(false); await handleStatus('Failed', failNote); setFailNote('');
  };

  if (loading || !order) return <LoadingOverlay />;

  const nextStatus = STATUS_FLOW[order.status];
  const isActive = !['Delivered', 'Failed'].includes(order.status);
  const avail = riders.filter(r => r.status === 'Available');

  // ✅ FIXED: was === 'doorstep', DB now stores 'Pickup'
  const isDoorstep = order.delivery_type === 'Pickup';
  const isPaid = order.payment_status === 'Paid';

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/orders')} style={{ marginBottom: 12 }}>Back to Orders</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <h1 className="page-title">Order #{order.id}</h1>
            <p className="page-sub">{order.package_id} · {moment(order.created_at).format('MMM D, YYYY h:mm A')}</p>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <StatusBadge status={order.status} />

            {/* Payment status badge */}
            <span style={{
              padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 700,
              background: isPaid ? '#F0FDF4' : '#FEF9EC',
              color: isPaid ? '#166534' : '#B45309',
              border: `1px solid ${isPaid ? '#BBF7D0' : '#FDE68A'}`
            }}>
              {isPaid ? 'Paid' : `Unpaid · ${order.payment_method}`}
            </span>

            {/* Mark as Paid button — show if not paid */}
            {!isPaid && (
              <button className="btn btn-secondary btn-md" onClick={handleMarkPaid} disabled={payLoading}>
                {payLoading ? <Spinner size={16} /> : 'Mark as Paid'}
              </button>
            )}

            {order.status === 'Pending' && (
              <button className="btn btn-secondary btn-md" onClick={() => setShowAssign(true)}>Assign Rider</button>
            )}
            {nextStatus && (
              <button className="btn btn-success btn-md" onClick={() => handleStatus(nextStatus)} disabled={actionLoading}>
                {actionLoading ? <Spinner size={16} /> : `Move to: ${nextStatus}`}
              </button>
            )}
            {isActive && order.status !== 'Pending' && (
              <button className="btn btn-danger btn-md" onClick={() => setShowFail(true)}>Mark Failed</button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>

        {/* Left */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ✅ Sender/Pickup details — now correctly shows for delivery_type === 'Pickup' */}
          {isDoorstep && (
            <div className="card" style={{ borderLeft: '3px solid var(--accent)' }}>
              <div className="card-header">
                <span className="card-title"> Pickup Details (Rider collects from sender)</span>
              </div>
              <div className="card-body">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                  {[
                    ['Sender Name', order.sender_name],
                    ['Sender Phone', order.sender_phone || '—'],
                    ['Pickup Address', order.pickup_address || '—'],
                  ].map(([k, v]) => (
                    <div key={k} style={{ padding: '10px 0', borderBottom: '1px solid var(--gray-100)', gridColumn: k === 'Pickup Address' ? 'span 2' : 'auto' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 3 }}>{k}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--gray-900)' }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Order details */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Order Details</span>
              {/* ✅ FIXED: badge now correctly reflects 'Pickup' or 'Office' */}
              <span style={{
                padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                background: isDoorstep ? '#EFF6FF' : '#F0FDF4',
                color: isDoorstep ? '#1D4ED8' : '#166534'
              }}>
                {isDoorstep ? 'Pickup' : 'Office'}
              </span>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                {[
                  ['Sender', order.sender_name],
                  ['Receiver', order.receiver_name],
                  ['Phone', order.phone],
                  ['Package ID', order.package_id],
                  ['Package Type', order.package_type],
                  ['Description', order.package_description || '—'],
                  ['Delivery Fee', `N${parseFloat(order.delivery_fee || 0).toLocaleString()}`],
                  ['Payment Method', order.payment_method],
                  ['Payment Status', order.payment_status],
                  ['Created By', order.created_by_name || '—'],
                ].map(([k, v]) => (
                  <div key={k} style={{ padding: '11px 0', borderBottom: '1px solid var(--gray-100)' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 3 }}>{k}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--gray-900)' }}>{v}</div>
                  </div>
                ))}
                <div style={{ padding: '11px 0', gridColumn: 'span 2' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 3 }}>Delivery Address</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--gray-900)' }}>{order.address}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment section */}
          <div className="card">
            <div className="card-header"><span className="card-title">Payment</span></div>
            <div className="card-body">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>Amount</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>
                    N{parseFloat(order.delivery_fee || 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>Method</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{order.payment_method}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>Status</div>
                  <div style={{
                    padding: '4px 14px', borderRadius: 99, fontSize: 13, fontWeight: 700, display: 'inline-block',
                    background: isPaid ? '#F0FDF4' : '#FEF9EC',
                    color: isPaid ? '#166534' : '#B45309',
                  }}>
                    {isPaid ? 'Paid' : 'Unpaid'}
                  </div>
                </div>
                {!isPaid && (
                  <button className="btn btn-success btn-md" onClick={handleMarkPaid} disabled={payLoading}>
                    {payLoading ? <Spinner size={16} /> : 'Mark as Paid'}
                  </button>
                )}
              </div>
              {order.payment_method === 'Cash' && !isPaid && (
                <div style={{ marginTop: 12, padding: '8px 12px', background: '#FEF9EC', borderRadius: 8, fontSize: 12, color: '#B45309', fontWeight: 500 }}>
                  Cash payment — mark as paid once rider collects cash from recipient.
                </div>
              )}
              {order.payment_method === 'Transfer' && !isPaid && (
                <div style={{ marginTop: 12, padding: '8px 12px', background: '#EFF6FF', borderRadius: 8, fontSize: 12, color: '#1D4ED8', fontWeight: 500 }}>
                  Transfer payment — mark as paid once you confirm payment received.
                </div>
              )}
            </div>
          </div>

          {/* Verification code */}
          <div className="card">
            <div className="card-header"><span className="card-title">Verification Code</span></div>
            <div className="card-body">
              {user.role === 'Admin' ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ background: 'var(--primary)', borderRadius: 10, padding: '14px 24px' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 28, fontWeight: 700, color: 'var(--accent)', letterSpacing: 6 }}>{order.verification_code}</span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--gray-500)', lineHeight: 1.5 }}>Admin view — always visible.<br />Share with customer at dispatch.</div>
                </div>
              ) : user.role === 'Dispatcher' ? (
                order.verification_code ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ background: 'var(--primary)', borderRadius: 10, padding: '14px 24px' }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 28, fontWeight: 700, color: 'var(--accent)', letterSpacing: 6 }}>{order.verification_code}</span>
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--success)', fontWeight: 600 }}>First time viewing — share with customer now!</div>
                    </div>
                    <div style={{ marginTop: 10, padding: '8px 12px', background: 'var(--warning-bg)', borderRadius: 8, fontSize: 12, color: 'var(--warning)', fontWeight: 500 }}>
                      This code is shown once. Refreshing will hide it permanently.
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 14, color: 'var(--gray-400)' }}>Code already viewed. Contact Admin if needed.</div>
                )
              ) : (
                <div style={{ fontSize: 14, color: 'var(--gray-400)' }}>Verification code not visible to riders.</div>
              )}
            </div>
          </div>

          {/* Status timeline */}
          <div className="card">
            <div className="card-header"><span className="card-title">Status Timeline</span></div>
            <div className="card-body">
              {['Pending', 'Assigned', 'Picked Up', 'In Transit', 'Delivered'].map((s, i) => {
                const statuses = ['Pending', 'Assigned', 'Picked Up', 'In Transit', 'Delivered', 'Failed'];
                const idx = statuses.indexOf(order.status);
                const done = i < idx || order.status === s;
                const cur = order.status === s;
                return (
                  <div key={s} style={{ display: 'flex', gap: 14, paddingBottom: i < 4 ? 16 : 0, position: 'relative' }}>
                    {i < 4 && <div style={{ position: 'absolute', left: 15, top: 30, bottom: 0, width: 2, background: done && !cur ? 'var(--success)' : 'var(--gray-200)' }} />}
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%', flexShrink: 0, zIndex: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                      background: cur ? 'var(--primary)' : done ? 'var(--success)' : 'var(--gray-100)',
                      border: `2px solid ${cur ? 'var(--accent)' : done ? 'var(--success)' : 'var(--gray-200)'}`
                    }}>
                      {done && !cur ? '✓' : ''}
                    </div>
                    <div style={{ paddingTop: 4 }}>
                      <div style={{ fontWeight: cur ? 700 : 500, color: cur ? 'var(--primary)' : done ? 'var(--gray-700)' : 'var(--gray-400)', fontSize: 13.5 }}>{s}</div>
                    </div>
                  </div>
                );
              })}
              {order.status === 'Failed' && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--danger-bg)', borderRadius: 8, borderLeft: '3px solid var(--danger)', color: 'var(--danger)', fontWeight: 700, fontSize: 13 }}>
                  Delivery Failed
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Package Label</span></div>
            <div className="card-body"><PrintLabel order={order} /></div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Assigned Rider</span></div>
            <div className="card-body">
              {order.rider_id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: 'var(--accent)' }}>
                    {order.rider_name?.charAt(0)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: 'var(--gray-900)' }}>{order.rider_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{order.rider_phone}</div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '10px 0' }}>
                  <p style={{ fontSize: 13, color: 'var(--gray-400)', marginBottom: 12 }}>No rider assigned yet</p>
                  <button className="btn btn-primary btn-sm" onClick={() => setShowAssign(true)}>Assign Rider</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assign Modal */}
      <Modal open={showAssign} onClose={() => setShowAssign(false)} title="Assign Rider"
        footer={<>
          <button className="btn btn-ghost btn-md" onClick={() => setShowAssign(false)}>Cancel</button>
          <button className="btn btn-primary btn-md" onClick={handleAssign} disabled={actionLoading || !selRider}>
            {actionLoading ? <Spinner size={16} /> : 'Assign'}
          </button>
        </>}>
        {avail.length === 0
          ? <p style={{ textAlign: 'center', padding: '20px 0', color: 'var(--gray-400)' }}>No riders available.</p>
          : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {avail.map(r => (
              <label key={r.user_id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                border: `2px solid ${selRider == r.user_id ? 'var(--accent)' : 'var(--gray-200)'}`,
                background: selRider == r.user_id ? 'var(--accent-glow)' : 'var(--white)', transition: 'all 0.15s'
              }}>
                <input type="radio" style={{ display: 'none' }} checked={selRider == r.user_id} onChange={() => setSelRider(r.user_id)} />
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, color: 'var(--accent)' }}>
                  {r.name?.charAt(0)}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{r.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{r.phone}</div>
                </div>
              </label>
            ))}
          </div>}
      </Modal>

      {/* Fail Modal */}
      <Modal open={showFail} onClose={() => setShowFail(false)} title="Mark as Failed"
        footer={<>
          <button className="btn btn-ghost btn-md" onClick={() => setShowFail(false)}>Cancel</button>
          <button className="btn btn-danger btn-md" onClick={handleFail}>Confirm Failed</button>
        </>}>
        <div className="form-group">
          <label className="form-label">Reason for failure</label>
          <textarea className="form-textarea" placeholder="e.g. Customer not available, wrong address..." value={failNote} onChange={e => setFailNote(e.target.value)} />
        </div>
      </Modal>
    </div>
  );
}